import Big from 'big.js';
import { toast } from 'vue3-toastify';
import { GetBestPromotionV2RequestDto, CreateCheckoutPreorderResponseDto, CreateCheckoutResponseDto, CreateCheckoutV2RequestDto } from '~/contract/dto/CheckoutRelated.dto';
import { CourierPackageItemDto } from '~/contract/dto/CourierRelated.dto';
import { FulfillPreOrderRequestDto, FulfillPreOrderResponseDto } from '~/contract/dto/FulfillmentRelated.dto';
import { CheckPromotionRequestDto, PromotionItemDto } from '~/contract/dto/PromotionRelated.dto';
import { TaxItemDto } from '~/contract/dto/TaxRelated.dto';
import { CartItemType, PaymentMethodSimple } from '~/contract/enum/CartRelated.enum';
import { PromotionType } from '~/contract/enum/PromotionRelated.enum';
import { AddressItemVo } from '~/contract/vo/UserAddressRelated.vo';
import ApiService from '~/service/ApiService';

const initialState = {
  selectedAddress: null as unknown as AddressItemVo,
  selectedPromotion: new Map<PromotionType, PromotionItemDto>(),
  selectedCourier: null as unknown as CourierPackageItemDto,
  selectedPayment: PaymentMethodSimple.AUTO as unknown as PaymentMethodSimple,
  useInsurance: false,
  usePoint: false,
  taxableTax: [] as TaxItemDto[],
  loading: false,
}

export const useCheckoutStore = defineStore('checkout', {
  state: () => (initialState),
  getters: {
    convertPromotionToAmount(state) {
      return Array.from(state.selectedPromotion.values())
        .map((i) => Big(i.amount).neg())
        .reduce((a, b) => Big(a).add(Big(b)), Big(0))
        .toNumber();
    },
    convertTaxToAmount(state) {
      return state.taxableTax
        .map((i) => Big(i.amount))
        .reduce((a, b) => Big(a).add(Big(b)), Big(0))
        .toNumber();
    },
  },
  actions: {
    async addPromotion(request: CheckPromotionRequestDto) {
      this.loading = true;
      const { data, pending, error, refresh }: any = await ApiService.query('/api/v1/checkout/promotion/check', request, true);
      this.loading = pending.value;
      if (error.value) {
        this.loading = false;
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as PromotionItemDto;
        this.selectedPromotion.set(response.promotionType, response);
      }
    },
    async retrieveTaxableTax(cartItemType: CartItemType) {
      this.loading = true;
      const { data, pending, error, refresh }: any = await ApiService.query('/api/v1/checkout/tax', { cartItemType }, true);
      this.loading = pending.value;
      if (error.value) {
        this.loading = false;
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as TaxItemDto[];
        this.taxableTax = response;
      }
    },
    async retrieveTaxableTaxFulfill(humanPreOrderId: string, selectedPreOrderItemIdList: Array<string>) {
      this.loading = true;
      const { data, pending, error, refresh }: any = await ApiService.post(
        `/api/v2/tax/preorder/${humanPreOrderId}/fulfillment`,
        { preOrderItemIdList: selectedPreOrderItemIdList },
        true,
      );
      this.loading = pending.value;
      if (error.value) {
        this.loading = false;
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as TaxItemDto[];
        this.taxableTax = response;
      }
    },
    async doCheckout(request: CreateCheckoutV2RequestDto) {
      this.loading = true;
      const { data, pending, error, refresh }: any = await ApiService.post('/api/v2/checkout', request, true);
      this.loading = pending.value;
      if (error.value) {
        this.loading = false;
        if (error.value.data?.humanOrderId) {
          useRouter()
            .push(`/transaction/${error.value.data?.humanOrderId}`)
            .then(() => {
              toast(error.value.data.message, { type: 'error' });
            });
        } else {
          toast(error.value.data.message, { type: 'error' });
        }
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as CreateCheckoutResponseDto;
        if (response.paymentUrl) {
          toast('Redirecting...', { type: 'success' });
          setTimeout(() => {
            window.location.href = response.paymentUrl;
          }, 750);
        } else {
          useRouter()
            .push(`/transaction/${response.humanOrderId}`)
            .then(() => {
              toast('Redirecting...', { type: 'success' });
            });
        }
      }
    },
    async doCheckoutPreorder(request: CreateCheckoutV2RequestDto) {
      this.loading = true;
      const { data, pending, error, refresh }: any = await ApiService.post('/api/v2/checkout/preorder', request, true);
      this.loading = pending.value;
      if (error.value) {
        this.loading = false;
        if (error.value.data?.humanPreOrderId) {
          useRouter()
            .push(`/preorder/${error.value.data?.humanPreOrderId}`)
            .then(() => {
              toast(error.value.data.message, { type: 'error' });
            });
        } else {
          toast(error.value.data.message, { type: 'error' });
        }
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as CreateCheckoutPreorderResponseDto;
        if (response.paymentUrl) {
          toast('Redirecting...', { type: 'success' });
          setTimeout(() => {
            window.location.href = response.paymentUrl;
          }, 750);
        } else {
          useRouter()
            .push(`/preorder/${response.humanPreOrderId}`)
            .then(() => {
              toast('Redirecting...', { type: 'success' });
            });
        }
      }
    },
    async doCheckoutFulfill(humanPreOrderId: string, request: FulfillPreOrderRequestDto) {
      this.loading = true;
      const { data, pending, error, refresh }: any = await ApiService.post(`/api/v2/transaction/preorder/${humanPreOrderId}/fulfillment`, request, true);
      this.loading = pending.value;
      if (error.value) {
        this.loading = false;
        if (error.value.data?.humanOrderId) {
          useRouter()
            .push(`/transaction/${error.value.data?.humanOrderId}`)
            .then(() => {
              toast(error.value.data.message, { type: 'error' });
            });
        } else {
          toast(error.value.data.message, { type: 'error' });
        }
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as FulfillPreOrderResponseDto;
        toast('Redirecting...', { type: 'success' });
        if (response.paymentUrl) {
          setTimeout(() => {
            window.location.href = response.paymentUrl;
          }, 750);
        } else {
          useRouter()
            .push(`/transaction/${response.humanOrderId}`)
        }
      }
    },
    async doRetryCheckout(humanOrderId: string) {
      this.loading = true;
      const { data, pending, error, refresh }: any = await ApiService.post(`/api/v1/checkout/retry/${humanOrderId}`, {}, true);
      this.loading = pending.value;
      if (error.value) {
        this.loading = false;
        if (error.value.data?.humanOrderId) {
          useRouter().push(`/transaction/${error.value.data?.humanOrderId}`);
        }
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as CreateCheckoutResponseDto;
        if (response.paymentUrl) {
          window.location.href = response.paymentUrl;
        }
      }
    },
    async doRetryCheckoutPreorder(humanPreOrderId: string) {
      this.loading = true;
      const { data, pending, error, refresh }: any = await ApiService.post(`/api/v2/checkout/preorder/retry/${humanPreOrderId}`, {}, true);
      this.loading = pending.value;
      if (error.value) {
        this.loading = false;
        if (error.value.data?.humanPreOrderId) {
          useRouter().push(`/transaction/${error.value.data?.humanPreOrderId}`);
        }
        throw error.value;
      }
      if (data.value && data.value.data) {
        const response = data.value.data as CreateCheckoutPreorderResponseDto;
        if (response.paymentUrl) {
          window.location.href = response.paymentUrl;
        }
      }
    },
    async getBestPromotion(request: GetBestPromotionV2RequestDto) {
      this.loading = true;
      const { data, pending, error, refresh }: any = await ApiService.post('/api/v2/checkout/best-promotion', request, true);
      this.loading = pending.value;
      if (error.value) {
        this.loading = false;
        throw error.value;
      }
      if (data.value && data.value.data) {
        return data.value.data as PromotionItemDto;
      }
      return null;
    },
    removePromotion(promotionHumanCode: string) {
      let promo = Array.from(this.selectedPromotion.values()).find((i) => i.humanCode == promotionHumanCode);
      this.selectedPromotion.delete(promo!.promotionType);
    },
    removePromotionByPromotionType(promotionType: PromotionType) {
      this.selectedPromotion.delete(promotionType);
    },
    resetState() {
      Object.assign(this, initialState);
    }
  },
});
